import { Reducer, createContext } from 'react';
import { Moderator } from 'services/moderator';
import { IFlatTraitDef, ITraitDefinition, QueueConfiguration, TenantFull } from '../../services';
import { Admin } from '../../services/admin';

export interface AdminState {
  queueDataState?: QueueConfiguration;
  tenantId?: string;
  tenantFull?: TenantFull;
  currentTenantDisplayName: string;
  userInput: string;
  saveActive: boolean;
  isUpdated: boolean;
  updateModerator: {
    userId?: string;
    isAdd?: boolean;
    isRemove?: boolean;
    isUpdate: boolean;
  };
  updateAdmin: {
    userId?: string;
    isAdd?: boolean;
    isRemove?: boolean;
    isUpdate: boolean;
  };
  adminSettingUpdateCount: number;
}

export type AdminAction =
  | { type: 'MODERATOR_FEEDBACK_COMMENT_OPTIONAL'; payload: boolean }
  | { type: 'CASE_OPEN_LIFETIME_IN_SECONDS'; payload: number }
  | { type: 'CASE_OPEN_LIFETIME_WARNING_FACTOR'; payload: number }
  | { type: 'CASE_ASSIGNED_LIFETIME_IN_SECONDS'; payload: number }
  | { type: 'CASE_OPEN_LIFETIME_MAX_IN_SECONDS'; payload: number }
  | { type: 'DEFAULT_CASE_COMPARER_ALGORITHM'; payload: string }
  | { type: 'DEFAULT_MAX_ASSIGNED_CASE_COUNT'; payload: number }
  | { type: 'DISPLAY_NAME'; payload: string }
  | { type: 'MAX_ALLOWED_LABELS'; payload: number }
  | { type: 'MAX_CARD_HEIGHT'; payload: number }
  | { type: 'MAX_MEDIA_HEIGHT'; payload: number }
  | { type: 'UPDATE_ALLOWED_MODERATORS'; payload: Moderator[] }
  | { type: 'UPDATE_ALLOWED_ADMINS'; payload: Admin[] }
  | { type: 'UPDATE_UXPREFERENCES_BLUR'; payload: boolean }
  | { type: 'UPDATE_UXPREFERENCES_BLUR_STRENGTH_PIXELS'; payload: number }
  | { type: 'UPDATE_UXPREFERENCES_GRAYSCALE_PERCENT'; payload: number }
  | { type: 'UPDATE_UXPREFERENCES_DESATURATE'; payload: boolean }
  | { type: 'UPDATE_UXPREFERENCES_FLIP'; payload: boolean }
  | { type: 'UPDATE_UXPREFERENCES_TEXT_BLUR'; payload: boolean }
  | { type: 'NOT_ASSIGNABLE'; payload: boolean }
  | { type: 'USER_INPUT'; payload: string }
  | { type: 'IS_ACTIVE'; payload: boolean }
  | { type: 'IS_UPDATED'; payload: boolean }
  | { type: 'UPDATE_ADMIN'; payload: { userId?: string; isAdd?: boolean; isRemove?: boolean; isUpdate: boolean } }
  | { type: 'UPDATE_MODERATOR'; payload: { userId?: string; isAdd?: boolean; isRemove?: boolean; isUpdate: boolean } }
  | { type: 'LOAD_QUEUE_CONFIG'; payload: QueueConfiguration }
  | { type: 'CLEAR_QUEUE_CONFIG' }
  | { type: 'UPDATE_TENANT_ID'; payload: string }
  | { type: 'UPDATE_TENANT'; payload: TenantFull }
  | { type: 'UPDATE_CURRENT_TENANT_DISPLAY_NAME'; payload: string }
  | { type: 'UPDATE_QUEUE_LIST_WITH_MODIFIED_QUEUE'; payload: QueueConfiguration }
  | { type: 'ANIMATION_LOOP_SPEED'; payload: number }
  | { type: 'TOGGLE_ANIMATION_LOOP'; payload: boolean }
  | { type: 'CATEGORIZE_TRAIT_DEFINITIONS'; payload: Record<string, Record<string, ITraitDefinition>> }
  | { type: 'TRAIT_DEFINITION'; payload: IFlatTraitDef };

export const adminReducer: Reducer<AdminState, AdminAction> = (state: AdminState, action: AdminAction): AdminState => {
  // These are all the actions that can happen without queueDataState
  // consider moving these to their own provider
  switch (action.type) {
    case 'USER_INPUT':
      return {
        ...state,
        userInput: action.payload
      };
    case 'IS_ACTIVE':
      return {
        ...state,
        saveActive: action.payload
      };
    case 'IS_UPDATED':
      return {
        ...state,
        isUpdated: action.payload
      };
    case 'UPDATE_ADMIN':
      return {
        ...state,
        updateAdmin: action.payload
      };
    case 'UPDATE_MODERATOR':
      return {
        ...state,
        updateModerator: action.payload
      };
    case 'LOAD_QUEUE_CONFIG':
      return {
        ...state,
        queueDataState: action.payload,
        tenantId: action.payload.tenant.tenantId
      };
    case 'CLEAR_QUEUE_CONFIG':
      return {
        ...state,
        queueDataState: undefined,
        tenantId: undefined,
        isUpdated: false,
        saveActive: false
      };
    case 'UPDATE_TENANT_ID':
      return {
        ...state,
        tenantId: action.payload
      };
    case 'UPDATE_TENANT':
      return {
        ...state,
        tenantFull: action.payload
      };
    case 'UPDATE_CURRENT_TENANT_DISPLAY_NAME':
      return {
        ...state,
        currentTenantDisplayName: action.payload
      };
    case 'UPDATE_QUEUE_LIST_WITH_MODIFIED_QUEUE':
      return {
        ...state,
        isUpdated: false,
        saveActive: false,
        adminSettingUpdateCount: state.adminSettingUpdateCount + 1,
        queueDataState: action.payload
      };
  }

  // We can't do any other admin updates until we load queue data
  if (!state.queueDataState) {
    return { ...state };
  }

  switch (action.type) {
    case 'MODERATOR_FEEDBACK_COMMENT_OPTIONAL':
      return {
        ...state,
        queueDataState: { ...state.queueDataState, moderatorFeedbackCommentOptional: action.payload }
      };
    case 'CASE_OPEN_LIFETIME_IN_SECONDS':
      return {
        ...state,
        queueDataState: { ...state.queueDataState, caseOpenLifetimeInSeconds: action.payload }
      };
    case 'CASE_OPEN_LIFETIME_WARNING_FACTOR':
      return {
        ...state,
        queueDataState: { ...state.queueDataState, caseOpenLifetimeWarningFactor: action.payload }
      };
    case 'CASE_ASSIGNED_LIFETIME_IN_SECONDS':
      return {
        ...state,
        queueDataState: { ...state.queueDataState, caseAssignedLifetimeInSeconds: action.payload }
      };
    case 'CASE_OPEN_LIFETIME_MAX_IN_SECONDS':
      return {
        ...state,
        queueDataState: { ...state.queueDataState, caseOpenLifetimeMaxInSeconds: action.payload }
      };
    case 'DEFAULT_CASE_COMPARER_ALGORITHM':
      return {
        ...state,
        queueDataState: { ...state.queueDataState, defaultCaseComparerAlgorithm: action.payload }
      };
    case 'DEFAULT_MAX_ASSIGNED_CASE_COUNT':
      return {
        ...state,
        queueDataState: { ...state.queueDataState, defaultMaxAssignedCaseCount: action.payload }
      };
    case 'UPDATE_ALLOWED_MODERATORS':
      return {
        ...state,
        queueDataState: { ...state.queueDataState, allowedModerators: action.payload }
      };
    case 'DISPLAY_NAME':
      return {
        ...state,
        queueDataState: { ...state.queueDataState, displayName: action.payload }
      };
    case 'NOT_ASSIGNABLE':
      return {
        ...state,
        queueDataState: { ...state.queueDataState, notAssignable: action.payload }
      };
    case 'UPDATE_UXPREFERENCES_BLUR_STRENGTH_PIXELS':
      return {
        ...state,
        queueDataState: {
          ...state.queueDataState,
          uxPreferences: {
            ...state.queueDataState.uxPreferences,
            subjectImage: { ...state.queueDataState.uxPreferences.subjectImage, blurStrengthPixels: action.payload }
          }
        }
      };
    case 'UPDATE_UXPREFERENCES_GRAYSCALE_PERCENT':
      return {
        ...state,
        queueDataState: {
          ...state.queueDataState,
          uxPreferences: {
            ...state.queueDataState.uxPreferences,
            subjectImage: { ...state.queueDataState.uxPreferences.subjectImage, grayScalePercent: action.payload }
          }
        }
      };
    case 'UPDATE_UXPREFERENCES_BLUR':
      return {
        ...state,
        queueDataState: {
          ...state.queueDataState,
          uxPreferences: {
            ...state.queueDataState.uxPreferences,
            subjectImage: { ...state.queueDataState.uxPreferences.subjectImage, isDefaultBlur: action.payload }
          }
        }
      };
    case 'UPDATE_UXPREFERENCES_DESATURATE':
      return {
        ...state,
        queueDataState: {
          ...state.queueDataState,
          uxPreferences: {
            ...state.queueDataState.uxPreferences,
            subjectImage: { ...state.queueDataState.uxPreferences.subjectImage, isDefaultGrayscale: action.payload }
          }
        }
      };
    case 'UPDATE_UXPREFERENCES_FLIP':
      return {
        ...state,
        queueDataState: {
          ...state.queueDataState,
          uxPreferences: {
            ...state.queueDataState.uxPreferences,
            subjectImage: { ...state.queueDataState.uxPreferences.subjectImage, isDefaultFlipped: action.payload }
          }
        }
      };
    case 'UPDATE_UXPREFERENCES_TEXT_BLUR':
      return {
        ...state,
        queueDataState: {
          ...state.queueDataState,
          uxPreferences: {
            ...state.queueDataState.uxPreferences,
            subjectImage: { ...state.queueDataState.uxPreferences.subjectImage, isTextBlurred: action.payload }
          }
        }
      };
    case 'MAX_ALLOWED_LABELS':
      return {
        ...state,
        queueDataState: {
          ...state.queueDataState,
          uxPreferences: {
            ...state.queueDataState.uxPreferences,
            decide: {
              ...state.queueDataState.uxPreferences.decide,
              maxAllowedLabels: action.payload
            }
          }
        }
      };
    case 'MAX_CARD_HEIGHT':
      return {
        ...state,
        queueDataState: {
          ...state.queueDataState,
          uxPreferences: {
            ...state.queueDataState.uxPreferences,
            maxCardHeight: action.payload
          }
        }
      };
    case 'MAX_MEDIA_HEIGHT':
      return {
        ...state,
        queueDataState: {
          ...state.queueDataState,
          uxPreferences: {
            ...state.queueDataState.uxPreferences,
            subjectImage: { ...state.queueDataState.uxPreferences.subjectImage, maxHeight: action.payload }
          }
        }
      };
    case 'UPDATE_ALLOWED_ADMINS':
      return {
        ...state,
        queueDataState: { ...state.queueDataState, allowedAdmins: action.payload }
      };
    case 'ANIMATION_LOOP_SPEED':
      return {
        ...state,
        queueDataState: {
          ...state.queueDataState,
          uxPreferences: {
            ...state.queueDataState.uxPreferences,
            subjectJson: {
              ...state.queueDataState.uxPreferences.subjectJson,
              animationLoopSpeed: action.payload
            }
          }
        }
      };
    case 'TOGGLE_ANIMATION_LOOP':
      return {
        ...state,
        queueDataState: {
          ...state.queueDataState,
          uxPreferences: {
            ...state.queueDataState.uxPreferences,
            subjectJson: {
              ...state.queueDataState.uxPreferences.subjectJson,
              animationLoopIsActive: action.payload
            }
          }
        }
      };
    case 'CATEGORIZE_TRAIT_DEFINITIONS':
      return {
        ...state,
        queueDataState: {
          ...state.queueDataState,
          categorizedTraitDefinitions: action.payload
        }
      };
    case 'TRAIT_DEFINITION': {
      const newCategoryDefinition = { ...state.queueDataState.categorizedTraitDefinitions };
      if (!newCategoryDefinition[action.payload.category]) {
        newCategoryDefinition[action.payload.category] = {};
      }
      newCategoryDefinition[action.payload.category][action.payload.trait] = {
        definition: action.payload.definition,
        examples: action.payload.examples,
        exemptions: action.payload.exemptions
      };
      return {
        ...state,
        queueDataState: {
          ...state.queueDataState,
          categorizedTraitDefinitions: newCategoryDefinition
        }
      };
    }
  }
};
//TRAIT_DEFINITION
export interface AdminContextProps {
  state: AdminState;
  dispatch: (action: AdminAction) => void;
}

export const AdminContext = createContext<AdminContextProps>({} as AdminContextProps);
